import { IconTypes, InvestorBadgeTypes } from '@genial/components';
import { Color, ColorTypes } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import {
  TransitOperPositionSuccessResponse,
  TransitOperProductPositionEntity,
  CustomerPendingRegistrationsSuccessResponse,
} from '@genial/services';
import {
  CustomerResponsesCustomerBlocksSection,
  CustomerResponsesInvestorProfileSection,
  ResponsesAssetPositionItemResponse,
  ResponsesAssetPositionResponse,
  ResponsesBlockedAssetsPositionResponse,
  ResponsesConsolidatedPositionResponse,
  ResponsesCustomerFinancialDataV3Response,
  ResponsesCustomerProductSummaryResponse,
  ResponsesLimitGenialBalanceResponse,
  ResponsesRecommendedPortfolioPositionResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import { getValidatedStatusIcon } from '../../utils/getValidatedStatusIcon';
import { normalizeInitials, normalizeV3Accounts } from '../customer/normalize';
import { extractPortfolioAssetsHashMap } from '../customerAssetDetail/normalize';
import {
  RecommendedPortfolioResponse,
  StrategyAssetsQueryItem,
} from '../customerAssetDetail/types';

import { getProductDistributionColor } from './constants';
import {
  ICustomerContractedProductsNormalized,
  ICustomerBlockedNormalized,
  ICustomerInvestedAssetsNormalized,
  ICustomerTransitNormalized,
  ICustomerGenialLimitNormalized,
  CustomerDetailFullAccount,
  PendingRegistrationReasonGroup,
} from './types';

type CustomerBlocksSectionRaw = CustomerResponsesCustomerBlocksSection & {
  pendingRegistrationReason?: Array<{
    type: number;
    reasons?: Array<{ id?: string; description?: string }> | null;
  }> | null;
  pendingRegistrationCheckError?: boolean | null;
};

export const normalizeCustomerTransits = (
  data: TransitOperPositionSuccessResponse['data']
): ICustomerTransitNormalized => {
  const totalNormalized = data.totalValue;

  const normalizeDescription = (operation: TransitOperProductPositionEntity) => {
    if (!operation.description) {
      return '--';
    }
    if (!operation.assetDescription) {
      return operation.description;
    }
    return `${operation.description} - Ativo: ${operation.assetDescription}`;
  };

  const operationsNormalized: ICustomerTransitNormalized['operationsNormalized'] = (
    data.transitOperProductPosition || []
  ).map((operation) => ({
    value: operation.value,
    settlementDate: operation.settlementDate,
    settlementDateNormalized: operation.settlementDate
      ? Utils.Mask.DateFormat(operation.settlementDate, 'dd/MM/yyyy')
      : 'A Definir',
    operationDescription: normalizeDescription(operation),
    typeDescription: operation.releaseTypeDescription || '--',
    classDescription: operation.assetClassificationDescription || '--',
  }));

  return {
    operationsNormalized,
    totalNormalized,
  };
};

export const normalizeCustomerBlocked = (
  data: ResponsesBlockedAssetsPositionResponse
): ICustomerBlockedNormalized => {
  const blockedAssetsNormalized: ICustomerBlockedNormalized['blockedAssetsNormalized'] = (
    data.blockedAssets || []
  ).map((operation) => ({
    value: operation.blockedValue,
    description: operation.assetName || '--',
  }));

  return {
    blockedAssetsNormalized,
  };
};

export const normalizeCustomerContractedProducts = (
  data: ResponsesCustomerProductSummaryResponse[]
): ICustomerContractedProductsNormalized => {
  return {
    contractedProductsAssetsNormalized: data.map((product) => ({
      productName: product.productName || '--',
      contracted: product.actived,
    })),
  };
};

export const normalizeCustomerGenialLimit = (
  data: ResponsesLimitGenialBalanceResponse
): ICustomerGenialLimitNormalized => {
  return {
    emptyGenialLimit: !data.hasLimitGenial,
    genialLimitNormalized: Utils.Mask.Money(data.formattedTotalGenialLimit), // Limite genial
    totalAllocatedNormalized: Utils.Mask.Money(data.formattedTotalAllocated), // Total alocado
    cardAllocatedNormalized: Utils.Mask.Money(data.currentCreditCardLimit), // Alocado no cartão
    traderAllocatedNormalized: Utils.Mask.Money(data.totalTrader), // Alocado em Trader Cockpit
    availableToAllocateNormalized: Utils.Mask.Money(data.allocationAvailable), // Disponível para alocação
    accountBalanceNormalized: Utils.Mask.Money(data.currentAccountBalance), // Saldo em conta
    availableBalanceNormalized: Utils.Mask.Money(data.totalAvailable), // Saldo Disponível para transação
  };
};

export const normalizeCustomerAssets = (
  data: ResponsesConsolidatedPositionResponse
): ICustomerInvestedAssetsNormalized => {
  const assetsNormalized: ICustomerInvestedAssetsNormalized['assetsNormalized'] = (
    data.consolidatedPosition.marketPosition || []
  ).map((position) => ({
    marketTypeNormalized: position.marketType || '--',
    marketNameNormalized: position.marketName || '--',
    percentageNormalized: position.percentageTotalValueInvested,
    valueNormalized: position.valueInvested,
    colorNormalized: getProductDistributionColor(position.marketType),
  }));

  return {
    totalValueNormalized: data.consolidatedPosition.totalNetValueInvested,
    assetsNormalized,
  };
};

export const normalizeCustomerBlockingReasons = (
  data: CustomerPendingRegistrationsSuccessResponse
): string[] => {
  return (
    data.data.pendings
      ?.map(
        (registration) => registration.reasons?.map((reason) => reason.description || '--') || []
      )
      .flat() || []
  );
};

type accountNumberType = string | number | null | undefined;
const normalizeAccount = (number: accountNumberType, digit: accountNumberType) => {
  if ((number === null || number === undefined) && (digit === null || digit === undefined)) {
    return '--';
  }
  if (digit === null || digit === undefined) {
    return `${number}`;
  }
  if (number === 0 && digit === 0) {
    return '--';
  }
  return `${number}-${digit}`;
};

const getInvestorType = (
  profile: CustomerResponsesInvestorProfileSection
): InvestorBadgeTypes.InvestorType => {
  if (profile.isProfessionalInvestor) {
    return InvestorBadgeTypes.InvestorType.Professional;
  } else if (profile.isQualifiedInvestor) {
    return InvestorBadgeTypes.InvestorType.Qualified;
  } else {
    return InvestorBadgeTypes.InvestorType.Common;
  }
};

const getInvestorTypeText = (
  profile: CustomerResponsesInvestorProfileSection
): InvestorBadgeTypes.InvestorTypeText => {
  if (profile.isProfessionalInvestor) {
    return InvestorBadgeTypes.InvestorTypeText.Professional;
  } else if (profile.isQualifiedInvestor) {
    return InvestorBadgeTypes.InvestorTypeText.Qualified;
  } else {
    return InvestorBadgeTypes.InvestorTypeText.Common;
  }
};

const normalizeMoneyField = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '--';
  }
  return Utils.Mask.Money(value);
};

const normalizeDateField = (value: Date | null | undefined): string => {
  if (value === null || value === undefined) {
    return '--';
  }
  return Utils.Mask.DateFormat(value, 'dd/MM/yyyy');
};

const normalizeBoolean = (value: boolean | null | undefined): string => {
  return value ? 'Sim' : 'Não';
};

const normalizeCustomerPendingReasons = (
  data: CustomerResponsesCustomerBlocksSection
): string[] => {
  if (!data || !data.pendingReasons) {
    return [];
  }
  return data.pendingReasons.filter(Boolean);
};

const normalizeCustomerPendingRegistrationReasons = (
  data: CustomerBlocksSectionRaw
): PendingRegistrationReasonGroup[] => {
  const raw = data.pendingRegistrationReason;
  if (!raw?.length) {
    return [];
  }

  return raw.map((group) => ({
    type: group.type,
    reasons: (group.reasons ?? [])
      .filter((reason) => reason.id && reason.description)
      .map((reason) => ({
        id: reason.id as string,
        description: reason.description as string,
      })),
  }));
};

const normalizeSuitabilityValidityText = (dayToExpiration: number | null | undefined): string => {
  if (dayToExpiration === null || dayToExpiration === undefined) {
    return '--';
  }
  const dayText = Math.abs(dayToExpiration) === 1 ? 'dia' : 'dias';
  if (dayToExpiration === 1) {
    return `1 ${dayText} para expirar suitability`;
  }
  if (dayToExpiration === 0) {
    return 'Expira hoje';
  }
  if (dayToExpiration < 0) {
    return `Expirado há ${Math.abs(dayToExpiration)} ${dayText}`;
  }
  return `${dayToExpiration} ${dayText} para expirar suitability`;
};

const normalizeAbleToOperateIcon = (
  enabled: boolean,
  icon: IconTypes.Props['name']
): IconTypes.Props => {
  return {
    name: icon,
    size: 'sm',
    color: enabled ? 'brand.tertiary.principal' : 'neutral.light',
  };
};

export const normalizeCustomerDetailFullAccountData = (
  data: ResponsesCustomerFinancialDataV3Response
): CustomerDetailFullAccount => {
  const accountAndEarnings = data.accountAndEarnings;
  const investorProfile = data.investorProfile;
  const enabledToOperateIn = data.enabledToOperateIn;
  const personalInformation = data.personalInformation;
  const institutionalData = data.institutionalData;
  const additionalAccounts = normalizeV3Accounts(data.additionalAccounts);

  const suitabilityIcon = getValidatedStatusIcon(investorProfile.suitabilityStatusIcon);
  const statusNormalized = data.accountAndEarnings.status.statusName?.toUpperCase() ?? '--';
  const customerBlocksRaw = data.customerBlocks as CustomerBlocksSectionRaw;

  return {
    additionalAccounts,
    accountAndEarnings: {
      ...accountAndEarnings,
      sinacorAccountNormalized: normalizeAccount(
        accountAndEarnings.accountNumber,
        accountAndEarnings.accountDigit
      ),
      pluralAccountNormalized: normalizeAccount(
        accountAndEarnings.pluralAccountNumber,
        accountAndEarnings.pluralAccountDigit
      ),
      digitalAccountNormalized: normalizeAccount(
        accountAndEarnings.digitalAccountNumber,
        accountAndEarnings.digitalAccountDigit
      ),
      pnpAccountNormalized: normalizeAccount(
        accountAndEarnings.pnpAccountNumber,
        accountAndEarnings.pnpAccountDigit
      ),
      monthlyIncomeNormalized: normalizeMoneyField(accountAndEarnings.monthlyIncome),
      yearlyIncomeNormalized: normalizeMoneyField(accountAndEarnings.annualIncome),
      netWorthNormalized: normalizeMoneyField(accountAndEarnings.netWorth),
      totalNetWorthNormalized: normalizeMoneyField(accountAndEarnings.totalNetWorth),
      otherIncomeNormalized: normalizeMoneyField(accountAndEarnings.otherIncome),
      registrationDateNormalized: normalizeDateField(accountAndEarnings.createdAt),
      updateDateNormalized: normalizeDateField(accountAndEarnings.updatedAt),
      advisorNameNormalized: accountAndEarnings.advisorName || '--',
      partnerNameNormalized: accountAndEarnings.partnerName || '--',
      statusNormalized: {
        ...accountAndEarnings.status,
        isBlocked: statusNormalized === 'BLOQUEADO',
        textNormalized: statusNormalized,
        textColorNormalized:
          statusNormalized !== 'REGULAR'
            ? Utils.ColorToken.fromColor(accountAndEarnings.status.iconColor)
            : 'neutral.black',
        iconColorNormalized: Utils.ColorToken.fromColor(accountAndEarnings.status.iconColor),
        iconNormalized: (accountAndEarnings.status.icon ?? 'Info') as IconographyTypes.Option,
        iconSizeNormalized: (accountAndEarnings.status.iconSize as IconTypes.Sizes) ?? 'xxxs',
      },
    },
    investorProfile: {
      ...investorProfile,
      investmentProfileNormalized: getInvestorType(investorProfile),
      investmentProfileTextNormalized: getInvestorTypeText(investorProfile),
      riskProfileDescriptionNormalized: investorProfile.riskProfileDescription || '--',
      suitabilityStatusNormalized: investorProfile.suitabilityStatus || '--',
      suitabilityStatusIcon: {
        name: suitabilityIcon.icon,
        size: suitabilityIcon.iconSize,
        backgroundColor: suitabilityIcon.backgroundColor,
        color: suitabilityIcon.iconColor,
      },
      suitabilityNormalized: investorProfile.suitabilityName || 'Desconhecido',
      suitabilityColorNormalized: Utils.Suitability.getCorrectColor(
        investorProfile.suitabilityName || ''
      ),
      suitabilityValidityNormalized: investorProfile.suitabilityValidity || '--',
      suitabilityDueDateNormalized: normalizeDateField(
        investorProfile.suitabilityProfileExpirationDate
      ),
      suitabilityValidityText: normalizeSuitabilityValidityText(
        investorProfile.suitabilityDayToExpiration
      ),
    },
    pendingReasons: normalizeCustomerPendingReasons(data.customerBlocks),
    pendingRegistrationReasons: normalizeCustomerPendingRegistrationReasons(customerBlocksRaw),
    pendingRegistrationCheckError: !!customerBlocksRaw.pendingRegistrationCheckError,
    productsToOperate: [
      {
        productName: 'HOMEBROKER',
        enabled: enabledToOperateIn.homeBrokerEnabled,
        icon: normalizeAbleToOperateIcon(enabledToOperateIn.homeBrokerEnabled, 'Homebroker'),
      },
      {
        productName: 'TESOURO DIRETO',
        enabled: enabledToOperateIn.treasuryDirectEnabled,
        icon: normalizeAbleToOperateIcon(
          enabledToOperateIn.treasuryDirectEnabled,
          'DirectTreasury'
        ),
      },
      {
        productName: 'RENDA FIXA',
        enabled: enabledToOperateIn.fixedIncomeEnabled,
        icon: normalizeAbleToOperateIcon(enabledToOperateIn.fixedIncomeEnabled, 'Stock'),
      },
    ],
    personalInformation: {
      ...personalInformation,
      initialsNormalized: normalizeInitials(personalInformation.fullName),
      fullNameNormalized: personalInformation.fullName || '--',
      cpfCnpjNormalized: Utils.Mask.CpfOrCnpj(personalInformation.cpfCnpj) || '--',
      rgNormalized: Utils.Mask.Rg(personalInformation.rg) || '--',
      phoneNumberNormalized: Utils.Mask.Phone(personalInformation.phone) || '--',
      emailNormalized: personalInformation.email || '--',
      maritalStatusNormalized: personalInformation.maritalStatus || '--',
      birthDateNormalized: normalizeDateField(personalInformation.birthDate),
      nationalityNormalized: personalInformation.nationality || '--',
      nifIndicatorNormalized: personalInformation.nifIndicator || '--',
      genderNormalized: personalInformation.gender || '--',
      professionNormalized: personalInformation.profession || '--',
    },
    institutionalData: {
      ...institutionalData,
      isCompany: personalInformation.cpfCnpj?.length === 14,
      exclusiveFundNormalized: normalizeBoolean(institutionalData?.exclusiveFund),
      nonProfitCompanyNormalized: normalizeBoolean(institutionalData?.nonProfitCompany),
      trustOrSimilarVehicleNormalized: normalizeBoolean(institutionalData?.trustOrSimilarVehicle),
      mainEconomicActivityNormalized: institutionalData?.mainEconomicActivity || '--',
    },
    representatives: data.representatives?.map((rep) => ({
      ...rep,
      representativeTypeNormalized: rep?.representativeType || '--',
      representativeNameNormalized: rep?.representativeName || '--',
      representativeCpfCnpjNormalized: Utils.Mask.CpfOrCnpj(rep?.representativeCpfCnpj || '--'),
    })),
    residentialData: {
      ...data.residentialData,
      zipCodeNormalized: data.residentialData.zipCode || '--',
      addressNormalized: data.residentialData.address || '--',
      addressNumberNormalized: data.residentialData.addressNumber || '--',
      neighborhoodNormalized: data.residentialData.neighborhood || '--',
      cityNormalized: data.residentialData.city || '--',
      stateNormalized: data.residentialData.state || '--',
      cityWithStateNormalized:
        data.residentialData.city && data.residentialData.state
          ? `${data.residentialData.city} - ${data.residentialData.state}`
          : '--',
    },
  };
};

export const orderAdditionalAccounts = (
  customerDetailed: CustomerDetailFullAccount,
  initialAccountNumber: number
): CustomerDetailFullAccount => {
  const accountSelected = customerDetailed.additionalAccounts.find(
    (account) => account.accountNumber === initialAccountNumber
  );

  if (!accountSelected) {
    return customerDetailed;
  }

  const additionalAccountsOrdered = [
    accountSelected,
    ...customerDetailed.additionalAccounts.filter(
      (account) => account.accountNumber !== initialAccountNumber
    ),
  ];

  return {
    ...customerDetailed,
    additionalAccounts: additionalAccountsOrdered,
  };
};

export const normalizeCustomerStrategyOrLiquidityPaymentHistory = (
  data: ResponsesAssetPositionItemResponse['paymentHistory']
): StrategyAssetsQueryItem['paymentHistoryNormalized'] => {
  if (!data) {
    return [];
  }

  return data.map((item) => ({
    ...item,
    eventNormalized: item.event || '--',
    dateNormalized: item.date ? Utils.Mask.DateFormat(item.date, 'dd/MM/yyyy') : '--',
    valueNormalized: Utils.Mask.Money(item.value),
  }));
};

export const normalizeCustomerStrategyOrLiquidityDetails = (
  data: ResponsesAssetPositionResponse
): StrategyAssetsQueryItem[] => {
  const items = data.items || [];

  const portfolioAssetsHashMap = extractPortfolioAssetsHashMap(data.recommendedPortfolios);

  return items.map((item) => ({
    ...item,
    paymentHistoryNormalized: normalizeCustomerStrategyOrLiquidityPaymentHistory(
      item.paymentHistory
    ),
    typeNameNormalized: item.categoryName || item.marketName || '--',
    assetNameNormalized: item.assetName || '--',
    marketNameNormalized: item.marketName || '--',
    categoryNameNormalized: item.categoryName || '--',
    productColor: Color[Utils.ColorToken.fromProductName(item.categoryTypeName)],
    grossValueNormalized: Utils.Mask.Money(item.grossValue),
    netValueNormalized: Utils.Mask.Money(item.netValue),
    incomeNormalized: Utils.Mask.Money(item.income),
    profitabilityNormalized: Utils.Mask.Percentage(item.profitability),
    maturityDateNormalized: item.maturityDate
      ? Utils.Mask.DateFormat(item.maturityDate, 'dd/MM/yyyy')
      : '--',
    hasRecommendedPortfolioLink: !!item.assetName && portfolioAssetsHashMap.has(item.assetName),
    recommendedPortfolioLinks: item.assetName
      ? portfolioAssetsHashMap.get(item.assetName) || []
      : [],
  }));
};

export const normalizeRecommendedPortfolioDetails = (
  data: ResponsesRecommendedPortfolioPositionResponse
): RecommendedPortfolioResponse => {
  const recommendedPortfolios = data.recommendedPortfolios || [];

  const portfolios = recommendedPortfolios.map((item) => ({
    ...item,
    assetNameNormalized: item.portfolioName || '--',
    appliedValueNormalized: Utils.Mask.Money(item.totalAppliedValue),
    profitabilityNormalized: item.profitability,
    profitabilityFormatted: Utils.Mask.Percentage(item.profitability),
    percentage: (item.totalInvestedValue * 100) / (data.totalInvestedValue || 1),
    assetsNormalized: (item.assets || []).map((asset) => ({
      assetNameNormalized: asset.assetCode || '--',
      assetCategoryNormalized: asset.category || '',
      appliedValueNormalized: Utils.Mask.Money(asset.appliedValue),
      grossValueNormalized: Utils.Mask.Money(asset.grossFinancialValue),
      profitabilityNormalized: asset.profitability || 0,
      profitabilityFormatted: Utils.Mask.Percentage(asset.profitability || 0),
      quantityNormalized: asset.quantity.toString(),
    })),
  }));
  return {
    generalInformation: {
      totalValue: data.totalInvestedValue || 0,
      portfoliosInvested: recommendedPortfolios
        .map((portfolio) => ({
          percentageNormalized:
            (portfolio.totalInvestedValue * 100) / (data.totalInvestedValue || 1),
          valueNormalized: portfolio.totalInvestedValue,
          marketNameNormalized: portfolio.portfolioName || '--',
          marketTypeNormalized: portfolio.id.toString(),
          colorNormalized: 'brand.secondary.principal',
        }))
        .sort((a, b) => b.percentageNormalized - a.percentageNormalized)
        .map((item, index) => ({
          ...item,
          colorNormalized: `scale.secondary.${9 - (index % 9)}00` as ColorTypes.Option,
        })),
    },
    portfolios,
  };
};
